import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Mic, Video, Brain, FileText, Menu, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { RecordingTab } from '@/components/session/tabs/RecordingTab';
import { GoogleMeetTab } from '@/components/session/tabs/GoogleMeetTab';
import { AIToolsTab } from '@/components/session/tabs/AIToolsTab';
import { NotesTab } from '@/components/session/tabs/NotesTab';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface Booking {
  id: string;
  client_id: string;
  practitioner_id: string;
  start_time: string;
  end_time: string;
  status: string;
  format: string;
  clients?: {
    first_name: string;
    last_name: string;
  };
  practitioners?: {
    first_name: string;
    last_name: string;
  };
}

export default function SessionToolsPage() {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('google-meet');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Get return path from URL params or default to upcoming
  const returnTo = searchParams.get('from') || 'upcoming';

  useEffect(() => {
    const fetchBooking = async () => {
      if (!bookingId || !user) return;

      try {
        // Use the same RPC function as other appointment components
        const { data, error } = await supabase
          .rpc('get_practitioner_bookings_with_client');

        if (error) throw error;

        // Find the specific booking by ID
        const booking = data?.find((b: any) => b.id === bookingId);
        if (!booking) {
          throw new Error('Booking not found');
        }

        // Transform to match expected structure
        const transformedBooking = {
          id: booking.id,
          client_id: booking.client_id,
          practitioner_id: user.id,
          start_time: booking.start_time,
          end_time: booking.end_time,
          status: booking.status,
          format: booking.format,
          clients: {
            first_name: booking.client_first_name,
            last_name: booking.client_last_name
          },
          practitioners: {
            first_name: user.user_metadata?.first_name || '',
            last_name: user.user_metadata?.last_name || ''
          }
        };

        setBooking(transformedBooking);
      } catch (error: any) {
        console.error('Error fetching booking:', error);
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Unable to load session details. You may not have permission to access this session.',
        });
        navigate(`/practitioner-dashboard?tab=${returnTo}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBooking();
  }, [bookingId, user, navigate, toast]);

  const formatDateTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'default';
      case 'scheduled':
        return 'secondary';
      case 'cancelled':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading session tools...</p>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Session not found</p>
          <Button onClick={() => navigate(`/practitioner-dashboard?tab=${returnTo}`)}>
            Return to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="flex h-screen">
        {/* Mobile Header */}
        <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-background border-b p-4 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <Menu className="h-4 w-4" />
          </Button>
          <h1 className="text-lg font-semibold">Session Tools</h1>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/practitioner-dashboard?tab=${returnTo}`)}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </div>

        {/* Sidebar */}
        <div className={`
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
          lg:translate-x-0 
          fixed lg:static 
          inset-y-0 left-0 
          w-72 border-r bg-card flex flex-col 
          transition-transform duration-300 ease-in-out 
          z-40
        `}>
          <div className="p-6 border-b">
            {/* Mobile close button */}
            <div className="lg:hidden flex justify-end mb-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <Button
              variant="ghost"
              onClick={() => navigate(`/practitioner-dashboard?tab=${returnTo}`)}
              className="w-full justify-start mb-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to {returnTo === 'upcoming' ? 'Upcoming' : 'History'}
            </Button>
            
            <div className="space-y-2">
              <h1 className="text-xl font-semibold">Session Tools</h1>
              <div className="space-y-1 text-sm text-muted-foreground">
                <p><span className="font-medium">Client:</span> {booking.clients?.first_name} {booking.clients?.last_name}</p>
                <p><span className="font-medium">Date:</span> {formatDateTime(booking.start_time)}</p>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Status:</span>
                  <Badge variant={getStatusVariant(booking.status)}>
                    {booking.status}
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex-1 p-4">
            <nav className="space-y-2">
              <Button
                variant={activeTab === 'google-meet' ? 'default' : 'ghost'}
                onClick={() => {
                  setActiveTab('google-meet');
                  setSidebarOpen(false);
                }}
                className="w-full justify-start"
              >
                <Video className="h-4 w-4 mr-2" />
                Google Meet
              </Button>
              <Button
                variant={activeTab === 'recording' ? 'default' : 'ghost'}
                onClick={() => {
                  setActiveTab('recording');
                  setSidebarOpen(false);
                }}
                className="w-full justify-start"
              >
                <Mic className="h-4 w-4 mr-2" />
                Recording
              </Button>
              <Button
                variant={activeTab === 'ai-tools' ? 'default' : 'ghost'}
                onClick={() => {
                  setActiveTab('ai-tools');
                  setSidebarOpen(false);
                }}
                className="w-full justify-start"
              >
                <Brain className="h-4 w-4 mr-2" />
                AI Tools
              </Button>
              <Button
                variant={activeTab === 'notes' ? 'default' : 'ghost'}
                onClick={() => {
                  setActiveTab('notes');
                  setSidebarOpen(false);
                }}
                className="w-full justify-start"
              >
                <FileText className="h-4 w-4 mr-2" />
                Notes
              </Button>
            </nav>
          </div>
        </div>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div 
            className="lg:hidden fixed inset-0 bg-black/50 z-30" 
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <div className="flex-1 flex flex-col lg:ml-0 pt-16 lg:pt-0">
          <div className="flex-1 overflow-auto">
            <div className="p-4 lg:p-6">
              {activeTab === 'recording' && <RecordingTab booking={booking} />}
              {activeTab === 'google-meet' && <GoogleMeetTab booking={booking} userRole="practitioner" />}
              {activeTab === 'ai-tools' && <AIToolsTab booking={booking} />}
              {activeTab === 'notes' && <NotesTab booking={booking} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}